import { ActionType, createAction, createAsyncAction } from "typesafe-actions";

import { OnlyRequiredFields } from "@mapmycustomers/shared";
import AnyColor from "@mapmycustomers/shared/types/AnyColor";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import { Territory } from "@mapmycustomers/shared/types/entity";
import { BusinessRule } from "@mapmycustomers/shared/types/entity/BusinessRule";
import { GeoMultiPolygon, GeoPolygon } from "@mapmycustomers/shared/types/shapes";
import AreaType from "@mapmycustomers/shared/types/territory/AreaType";
import User from "@mapmycustomers/shared/types/User";
import { ListRequest } from "@mapmycustomers/shared/types/viewModel/internalModel/ListRequest";

export const fetchAllTerritories = createAsyncAction(
  "@territories/fetchAll/request",
  "@territories/fetchAll/success",
  "@territories/fetchAll/failure"
)<{ force: boolean } | void, void, void>();

export const fetchTerritories = createAsyncAction(
  "@territories/fetch/request",
  "@territories/fetch/success",
  "@territories/fetch/failure"
)<
  { request?: Partial<ListRequest> },
  { countOnly?: boolean; territories: Territory[]; total: number },
  unknown
>();

export const createTerritory = createAsyncAction(
  "@territories/create/request",
  "@territories/create/success",
  "@territories/create/failure"
)<
  {
    businessRules?: OnlyRequiredFields<BusinessRule, "filters" | "name" | "territories">[];
    onFailure?: () => void;
    onSuccess: (newTerritory: Territory) => void;
    shape?: GeoMultiPolygon | GeoPolygon;
    // set when the caller reports failures itself, to avoid a toast per failed row
    suppressErrorNotification?: boolean;
    territory: {
      adm1: string[];
      adm2: string[];
      adm3: string[];
      color?: AnyColor;
      coordinates: LongLat[][];
      hidden?: boolean;
      name: string;
      opacity: number;
      requireRuleAssignment: boolean;
      type: "points" | AreaType;
    };
    userIdsToShareWith?: User["id"][];
  },
  { territory: Territory },
  unknown
>();

export const updateTerritory = createAsyncAction(
  "@territories/update/request",
  "@territories/update/success",
  "@territories/update/failure"
)<
  {
    onFailure?: () => void;
    onSuccess?: (updatedTerritory: Territory) => void;
    shape?: GeoMultiPolygon | GeoPolygon;
    // set when the caller reports failures itself, to avoid a toast per failed row
    suppressErrorNotification?: boolean;
    territory: Territory;
    userIdsToShareWith?: User["id"][];
  },
  { territory: Territory },
  void
>();

export const deleteTerritory = createAsyncAction(
  "@territories/delete/request",
  "@territories/delete/success",
  "@territories/delete/failure"
)<
  {
    onSuccess?: (territoryId: Territory["id"]) => void;
    territoryId: Territory["id"];
  },
  { territoryId: Territory["id"] },
  unknown
>();

export const updateTerritorySharing = createAsyncAction(
  "@territories/updateSharing/request",
  "@territories/updateSharing/success",
  "@territories/updateSharing/failure"
)<
  {
    onFailure?: () => void;
    onSuccess?: (updatedTerritory: Territory) => void;
    // set when the caller reports failures itself, to avoid a toast per failed row
    suppressErrorNotification?: boolean;
    territory: Pick<Territory, "id" | "userIds">;
    userIdsToShareWith: User["id"][];
  },
  { territory: Territory },
  unknown
>();

export const hideTerritories = createAction("@territories/hideTerritories")<Territory["id"][]>();

export const showTerritories = createAction("@territories/showTerritories")<Territory["id"][]>();

export const ensureTerritoriesHaveShape = createAction("@territories/ensureTerritoriesHaveShape")<
  Territory[]
>();

export const fetchTerritoryShapes = createAsyncAction(
  "@territories/fetchTerritoryShapes/request",
  "@territories/fetchTerritoryShapes/success",
  "@territories/fetchTerritoryShapes/failure"
)<Territory[], Territory[], Territory[]>();

export type Actions = ActionType<
  | typeof createTerritory
  | typeof deleteTerritory
  | typeof ensureTerritoriesHaveShape
  | typeof fetchAllTerritories
  | typeof fetchTerritories
  | typeof fetchTerritoryShapes
  | typeof hideTerritories
  | typeof showTerritories
  | typeof updateTerritory
  | typeof updateTerritorySharing
>;
